import React from "react";
import PopupModules from "src/features/auth/components/PopupModules";
import { Button } from "src/business-types/grocery/components/Button";
import { ThemeBasedColorHeader, ThemeBasedColorMenubar } from "src/shared/modules/profile/theme/themeCheacker";
import { CategoryV2 } from "src/shared/services/interfacev2";
import { ProductFormState } from "./useProductForm";
import ProductFormFields from "./ProductFormFields";

type Props = {
    isOpen: boolean;
    title: string;
    productType: "Inward" | "Service";
    switchProductType: (type: "Inward" | "Service") => void;
    form: ProductFormState;
    setField: <K extends keyof ProductFormState>(key: K, val: ProductFormState[K]) => void;
    setForm: React.Dispatch<React.SetStateAction<ProductFormState>>;
    category: CategoryV2[];
    uom: { id: string; uomName: string }[];
    barcodeMode: boolean;
    scanMode: boolean;
    onToggleScan: () => void;
    onBarcodeDetected: (code: string) => void;
    onImageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    discountPrice: number;
    finalPrice: number;
    sellingPrice: number;
    purchasePrice: number;
    wholeSalePrice: number;
    canSubmit: boolean;
    submit: boolean;
    onSubmit: () => void;
    onClose: () => void;
    serverImageUrl?: string;
    // AddProducts only
    multiAdd?: boolean;
    showMultiAdd?: boolean;
    onMultiAddChange?: (v: boolean) => void;
};

export default function ProductFormModal({
    isOpen, title, productType, switchProductType,
    form, setField, setForm, category, uom,
    barcodeMode, scanMode, onToggleScan, onBarcodeDetected, onImageChange,
    discountPrice, finalPrice, sellingPrice, purchasePrice, wholeSalePrice,
    canSubmit, submit, onSubmit, onClose,
    serverImageUrl,
    multiAdd, showMultiAdd, onMultiAddChange,
}: Props) {
    return (
        <PopupModules isOpen={isOpen} onClose={onClose} title={title}>
            <div className="w-full max-w-md space-y-3">
                {/* Product type toggle */}
                <div className="flex gap-2 mb-3">
                    {(["Inward", "Service"] as const).map((type) => (
                        <button
                            key={type}
                            onClick={() => switchProductType(type)}
                            className={`flex-1 py-1.5 rounded-md text-sm font-medium ${productType === type ? ThemeBasedColorHeader() : ThemeBasedColorMenubar()
                                }`}
                        >
                            {type} Products
                        </button>
                    ))}
                </div>

                <ProductFormFields
                    form={form}
                    setField={setField}
                    setForm={setForm}
                    productType={productType}
                    category={category}
                    uom={uom}
                    barcodeMode={barcodeMode}
                    scanMode={scanMode}
                    onToggleScan={onToggleScan}
                    onBarcodeDetected={onBarcodeDetected}
                    onImageChange={onImageChange}
                    discountPrice={discountPrice}
                    finalPrice={finalPrice}
                    sellingPrice={sellingPrice}
                    purchasePrice={purchasePrice}
                    wholeSalePrice={wholeSalePrice}
                    serverImageUrl={serverImageUrl}
                />

                {/* Multi-add checkbox (Add mode only) */}
                {showMultiAdd && (
                    <div className="flex items-center gap-2 mt-2">
                        <input
                            type="checkbox"
                            checked={multiAdd}
                            onChange={() => onMultiAddChange?.(!multiAdd)}
                            className="w-5 h-5"
                        />
                        <p className="text-sm">Add Multiple Products</p>
                    </div>
                )}

                {/* Action buttons */}
                <div className="flex justify-center gap-3 mt-3">
                    <Button label="Cancel" variant="ThemeCancel" className="border" onClick={onClose} disabled={submit} />
                    <Button
                        label={submit ? "Loading ...." : "Confirm"}
                        variant="ThemeBase"
                        onClick={onSubmit}
                        disabled={!canSubmit}
                    />
                </div>
            </div>
        </PopupModules>
    );
}